import { useEffect, useState } from "react";
import OffConvas from "../../components/OffConvas";
import UserForm from "./components/UserForm";
import { userService } from "../../services/userService";
import DeleteConfirmModal from "../../components/DeleteConfirmModal";
import { toast } from "../../services/toastService";
import Pagination from "../../components/Pagination";
import Loading from "../../components/Loading";

const Users = () => {
  const [data, setData] = useState([]);
  const [id, setId] = useState(0);
  const [deleteId, setDeleteId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (pageNumber = 1) => {
    try {
      setLoading(true);
      const res = await userService.getAll({
        pageNumber: pageNumber,
        pageSize: 15,
        orderBy: "id",
        orderDirection: "desc",
      });
      setData(res.value);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAsync = async () => {
    try {
      setLoading(true);
      if (!deleteId) {
        toast.error("Please select a record to delete");
        return;
      }
      var result = await userService.delete(deleteId);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to delete record");
        return;
      }
      toast.success("Record deleted successfully");
      setDeleteId(0);
      if (!search) loadData(data.pageNumber);
      else getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while deleting the record");
    } finally {
      setLoading(false);
    }
  };

  const getAllSearchAsync = async (pageNumber = 1, signal) => {
    try {
      var res = await userService.getAllSearchAsync(
        {
          search: search,
          pageNumber: pageNumber,
          pageSize: 15,
          orderBy: "id",
          orderDirection: "desc",
        },
        signal
      );
      setData(res.value);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search.trim().length < 2) {
      loadData(1);
      return;
    }

    const controller = new AbortController();
    const delay = setTimeout(() => {
      getAllSearchAsync(1, controller.signal);
    }, 400);

    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [search]);

  const onPageChange = async (pageNumber) => {
    setLoading(true);
    if (!search) {
      loadData(pageNumber);
      return;
    }
    await getAllSearchAsync(pageNumber);
  };

  const toggleStatusAsync = async (record) => {
    try {
      setLoading(true);
      const result = await userService.changeStatus(record.id, !record.isActive);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to update status");
        return;
      }
      toast.success("Status updated successfully");
      if (!search) loadData(data.pageNumber);
      else getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while updating the status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <section className="section-documents">
        <div className="my-card">
          <div className="my-card-header">
            <div className="my-card-title">Users</div>
            <button
              type="button"
              className="my-button my-button-dark"
              data-bs-toggle="offcanvas"
              data-bs-target="#my-user"
            >
              Add New
            </button>
          </div>
          <div className="my-card-body">
            <div className="my-grid">
              <div className="my-grid-header">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="form-control my-grid-search"
                />
              </div>
              <div className="my-grid-body">
                <table className="table table-bordered table-responsive">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((record) => (
                      <tr key={record.id}>
                        <td>{record.name}</td>
                        <td>{record.userName}</td>
                        <td>{record.email}</td>
                        <td>{record.roles?.join(", ")}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              role="switch"
                              checked={!!record.isActive}
                              onChange={() => toggleStatusAsync(record)}
                            />
                          </div>
                        </td>
                        <td className="my-grid-actions">
                          <i
                            className="fas fa-pencil text-primary cursor-pointer"
                            data-bs-toggle="offcanvas"
                            data-bs-target="#my-user"
                            onClick={() => setId(record.id)}
                          />
                          <i
                            className="fas fa-trash text-danger cursor-pointer"
                            data-bs-toggle="modal"
                            data-bs-target="#confirm-modal"
                            onClick={() => setDeleteId(record.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="my-grid-footer">
                <Pagination
                  currentPage={data.pageNumber}
                  totalPages={data.totalPages}
                  onPageChange={onPageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <DeleteConfirmModal deleteAsync={deleteAsync} />
      <OffConvas title="User" id="my-user">
        <UserForm loadData={loadData} id={id} setId={setId} />
      </OffConvas>
    </>
  );
};

export default Users;
